/**
 * quantity-guard.js
 * -----------------------------------------------------------------------------
 * Fixes three problems with the quantity selector on collection cards, search
 * results, the cart page and the cart drawer.
 *
 *  1. RAPID CLICKING
 *     Dawn's <quantity-input> component (assets/global.js) steps the value on
 *     EVERY click with no cooldown. Machine-gun clicking spikes the quantity
 *     and fires a burst of cart AJAX calls. Fixed with a leading-edge throttle:
 *     the first click is instant, further clicks are ignored until the cooldown
 *     elapses. Clicking still feels smooth, it just can't run away.
 *
 *  2. TYPING HUGE VALUES
 *     Shopify only renders a `max` attribute when the variant has a
 *     quantity_rule.max, which is null for almost every product — so the input
 *     had no upper bound and a customer could type 300000. (The cart handler in
 *     cart-notification.js already silently clamped to 99 via
 *     `dataset.max || 99`, so the field and the cart disagreed.) Fixed by
 *     applying the same effective max of 99 to the input itself.
 *
 *  3. QUANTITY JUMPING BACKWARDS  <-- the "reaches 21 then shows 1-2-3" bug
 *     Two separate handlers respond to every +/- click on a card that is
 *     already in the cart (`.quantity-add-to-cart-btns.added-item`):
 *
 *       a) cart-drawer.js `click` handler -> scheduleCartSync(). This one is
 *          already correct: debounced 300ms AND sequence-guarded, so an old
 *          response can't overwrite a newer one.
 *
 *       b) cart-notification.js `change` handler (the listener registered
 *          around line 157). Dawn dispatches a `change` event on every single
 *          click, and this handler has NO debounce and NO sequence guard. Each
 *          click spawns its own `/cart.js` -> `/cart/change.js` chain.
 *
 *     Those (b) chains resolve out of order. syncProductCardCartState() in
 *     cart.js then writes `quantityInput.value = cartItem.quantity` using
 *     whatever the server reported at that moment, so a late response carrying
 *     an older quantity clobbers what is on screen. In the 28 Jul recording the
 *     value goes 29 -> 30 -> 31 -> 20 -> 21 -> 22: a response from about eleven
 *     clicks earlier landed last and won.
 *
 *     Fixed here by giving path (b) the same protection path (a) already has:
 *     the `change` event is swallowed and re-dispatched once the customer stops
 *     clicking, and while that is pending the input is protected from
 *     programmatic writes, which is what a stale cart response is.
 *
 * HOW IT HOOKS IN
 *     Everything is delegated from `document` in the CAPTURE phase, so it runs
 *     BEFORE Dawn's button listeners and before the theme's own bubble-phase
 *     `change` listeners, and it keeps working after a section is re-rendered
 *     by AJAX. No changes to global.js, cart.js, cart-drawer.js or
 *     cart-notification.js are needed.
 *
 * Loaded from layout/theme.liquid.
 * -----------------------------------------------------------------------------
 */

(function () {
  'use strict';

  /* ==========================================================================
     SETTINGS
     ========================================================================== */

  // Minimum gap between two accepted +/- steps, in milliseconds.
  //   200 = quick but controlled   |   250 = recommended   |   400 = deliberate
  var CLICK_COOLDOWN_MS = 250;

  // Upper limit used when the product has no quantity_rule.max from Shopify.
  // Matches the ceiling cart-notification.js already applies internally.
  var DEFAULT_MAX_QTY = 99;

  // How long to wait after the last change before letting the theme talk to
  // the cart. Must comfortably exceed cart-drawer.js's CART_SYNC_DEBOUNCE (300)
  // so the cart has settled before the second sync path reads /cart.js.
  var CART_SYNC_DEBOUNCE_MS = 700;

  // Show a short "Maximum quantity is N" bubble when someone hits the ceiling.
  var SHOW_LIMIT_HINT = true;

  /* ========================================================================== */

  var QTY_SELECTOR = 'quantity-input input[type="number"]';

  var lastStepAt = new WeakMap(); // wrapper -> timestamp of last accepted step
  var pendingSync = new WeakMap(); // input   -> pending re-dispatch timeout id
  var protectedInputs = new WeakSet();
  var internalWrite = false; // true while THIS script is writing a value

  var nativeValue =
    window.HTMLInputElement && Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');

  function isQtyInput(el) {
    return el && el.matches && el.matches(QTY_SELECTOR);
  }

  /**
   * Write a value without tripping our own stale-write protection.
   */
  function setValue(input, value) {
    internalWrite = true;
    try {
      input.value = value;
    } finally {
      internalWrite = false;
    }
  }

  /**
   * Read the effective min / max / step for an input, falling back to the
   * defaults above when Shopify did not render a max.
   */
  function limitsFor(input) {
    var min = parseInt(input.getAttribute('min') || input.dataset.min, 10);
    if (isNaN(min) || min < 0) min = 0;

    var max = parseInt(input.getAttribute('max') || input.dataset.max, 10);
    if (isNaN(max) || max <= 0) max = DEFAULT_MAX_QTY;
    if (max > DEFAULT_MAX_QTY) max = DEFAULT_MAX_QTY;

    var step = parseInt(input.getAttribute('step'), 10);
    if (isNaN(step) || step < 1) step = 1;

    return { min: min, max: max, step: step };
  }

  /**
   * Snap a value into range, respecting the step increment.
   */
  function clamp(value, limits) {
    if (value > limits.max) value = limits.max;
    if (limits.step > 1) {
      var offset = value - limits.min;
      if (offset > 0) value = limits.min + Math.floor(offset / limits.step) * limits.step;
    }
    if (value < limits.min) value = limits.min;
    return value;
  }

  /* --------------------------------------------------------------------------
     Hint bubble
     -------------------------------------------------------------------------- */

  // Lives on <body> with `position: fixed`. An absolutely-positioned bubble
  // inside the product card gets clipped by the card's `overflow: hidden`,
  // which showed up as a stray black bar across the product title.
  var hintEl = null;
  var hintTimer = null;

  function flashHint(wrapper, message) {
    if (!SHOW_LIMIT_HINT || !wrapper) return;

    if (!hintEl) {
      hintEl = document.createElement('div');
      hintEl.className = 'qty-guard-hint';
      hintEl.setAttribute('role', 'status');
      document.body.appendChild(hintEl);
    }

    hintEl.textContent = message;

    hintEl.classList.add('is-measuring');
    var width = hintEl.offsetWidth;
    var height = hintEl.offsetHeight;
    hintEl.classList.remove('is-measuring');

    var rect = wrapper.getBoundingClientRect();
    var left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));

    var top = rect.top - height - 8;
    if (top < 8) top = rect.bottom + 8; // flip below if there's no room above

    hintEl.style.left = left + 'px';
    hintEl.style.top = top + 'px';
    hintEl.classList.add('is-visible');

    clearTimeout(hintTimer);
    hintTimer = setTimeout(function () {
      hintEl.classList.remove('is-visible');
    }, 1800);
  }

  function markCooling(wrapper) {
    wrapper.classList.add('qty-guard-cooling');
    setTimeout(function () {
      wrapper.classList.remove('qty-guard-cooling');
    }, CLICK_COOLDOWN_MS);
  }

  function blockEvent(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  /* --------------------------------------------------------------------------
     1. Throttle the +/- buttons
     -------------------------------------------------------------------------- */

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest && event.target.closest('quantity-input button[name]');
      if (!button) return;

      var wrapper = button.closest('quantity-input');
      var input = wrapper && wrapper.querySelector('input[type="number"]');
      if (!input) return;

      var now = Date.now();
      var last = lastStepAt.get(wrapper) || 0;

      // Too soon after the previous step — swallow this click entirely.
      if (now - last < CLICK_COOLDOWN_MS) {
        blockEvent(event);
        markCooling(wrapper);
        return;
      }

      // Enforce our effective ceiling on the plus button, because Dawn's
      // stepUp() only respects a `max` attribute that usually isn't there.
      if (button.name === 'plus') {
        var limits = limitsFor(input);
        var current = parseInt(input.value, 10) || 0;
        if (current + limits.step > limits.max) {
          blockEvent(event);
          setValue(input, clamp(current, limits));
          flashHint(wrapper, 'Maximum quantity is ' + limits.max);
          return;
        }
      }

      lastStepAt.set(wrapper, now);
    },
    true // capture — runs before Dawn's listener on the button itself
  );

  /* --------------------------------------------------------------------------
     2. Restrict what can be typed / pasted / scrolled into the field
     -------------------------------------------------------------------------- */

  document.addEventListener(
    'keydown',
    function (event) {
      if (!isQtyInput(event.target)) return;

      if (['e', 'E', '+', '-', '.', ','].indexOf(event.key) !== -1) {
        event.preventDefault();
        return;
      }

      // Holding ArrowUp / ArrowDown auto-repeats ~30x a second. Allow the
      // first press, ignore the repeats.
      if (event.repeat && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault();
      }
    },
    true
  );

  // Mouse wheel over a focused number input silently changes it. Turn that off.
  document.addEventListener(
    'wheel',
    function (event) {
      if (isQtyInput(event.target) && document.activeElement === event.target) {
        event.target.blur();
      }
    },
    { passive: true }
  );

  // Live sanitising while typing: digits only, never above the ceiling.
  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!isQtyInput(input)) return;

    var raw = String(input.value).replace(/[^\d]/g, '');
    if (raw === '') {
      if (input.value !== '') setValue(input, '');
      return; // let them clear the field mid-edit; `change` will fix it
    }

    var limits = limitsFor(input);
    var value = parseInt(raw, 10);

    if (value > limits.max) {
      value = limits.max;
      flashHint(input.closest('quantity-input'), 'Maximum quantity is ' + limits.max);
    }

    if (String(value) !== input.value) setValue(input, value);
  });

  // Paste: strip anything that isn't a digit before it lands in the field.
  document.addEventListener('paste', function (event) {
    var input = event.target;
    if (!isQtyInput(input)) return;

    var text = (event.clipboardData || window.clipboardData).getData('text') || '';
    var digits = text.replace(/[^\d]/g, '');
    event.preventDefault();
    if (!digits) return;

    setValue(input, clamp(parseInt(digits, 10), limitsFor(input)));
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });

  /**
   * Snap to a legal value. Used by both `change` and `blur`.
   */
  function normalise(input) {
    var limits = limitsFor(input);
    var value = parseInt(input.value, 10);
    var wasEmptyOrInvalid = isNaN(value);
    if (wasEmptyOrInvalid) value = limits.min;

    var corrected = clamp(value, limits);
    if (corrected !== value || wasEmptyOrInvalid || input.value === '') {
      setValue(input, corrected);
      if (!wasEmptyOrInvalid && value > limits.max) {
        flashHint(input.closest('quantity-input'), 'Maximum quantity is ' + limits.max);
      }
    }
  }

  /* --------------------------------------------------------------------------
     3. Stop stale cart responses from rewinding the on-screen quantity
     -------------------------------------------------------------------------- */

  /**
   * While the customer is mid-edit, ignore any value pushed into the field
   * from outside this script. A cart response that lands during that window
   * is, by definition, reporting a quantity the customer has already moved
   * past — that is the 31 -> 20 rewind.
   *
   * Note this deliberately does NOT block Dawn's stepUp()/stepDown(), which
   * set the value natively rather than through this accessor.
   */
  function protectFromStaleWrites(input) {
    if (!nativeValue || protectedInputs.has(input)) return;
    protectedInputs.add(input);

    Object.defineProperty(input, 'value', {
      configurable: true,
      enumerable: true,
      get: function () {
        return nativeValue.get.call(this);
      },
      set: function (v) {
        if (!internalWrite && pendingSync.has(this)) return; // stale — drop it
        nativeValue.set.call(this, v);
      },
    });
  }

  /**
   * Does this input drive a cart request through cart-notification.js?
   * The cart page and drawer use name="updates[]" and are handled by Dawn's
   * own debounced cart component, so they're left alone.
   */
  function drivesCartSync(input) {
    return input.classList.contains('card-quantity-input') || input.classList.contains('custom-quantity-input');
  }

  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!isQtyInput(input)) return;

      normalise(input);

      // Our own re-dispatch — let it through to the theme's cart handlers.
      if (input.qtyGuardSynthetic) {
        input.qtyGuardSynthetic = false;
        return;
      }

      if (!drivesCartSync(input)) return;

      // Swallow this change so cart-notification.js's un-sequenced
      // /cart.js -> /cart/change.js chain never starts, then re-dispatch once
      // the customer has actually stopped clicking.
      event.stopPropagation();

      protectFromStaleWrites(input);

      var wrapper = input.closest('quantity-input');
      if (wrapper) wrapper.classList.add('qty-guard-syncing');

      clearTimeout(pendingSync.get(input));
      pendingSync.set(
        input,
        setTimeout(function () {
          pendingSync.delete(input);
          if (wrapper) wrapper.classList.remove('qty-guard-syncing');
          input.qtyGuardSynthetic = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }, CART_SYNC_DEBOUNCE_MS)
      );
    },
    true
  );

  // Safety net. A number input silently discards anything it can't parse
  // (e.g. "12abc"), leaving the field visually blank. Restore the minimum
  // when focus leaves so the field is never submitted empty.
  document.addEventListener(
    'blur',
    function (event) {
      var input = event.target;
      if (!isQtyInput(input)) return;

      var before = input.value;
      normalise(input);
      if (input.value !== before) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
    true // capture — `blur` does not bubble
  );
})();